import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { getWelcomeScreenHtml } from './welcomeScreenConfig';
/* eslint-disable max-len */
import { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, Divider, Link, Stack, Typography, } from '@mui/material';
import { replaceStylesheetGlobals } from '../portal/theme/portalTheme';
import Conversation, { sharedConversationMeta } from './Conversation';
import buildSx from './buildSx';
import EmbeddedHeader from './EmbeddedHeader';
import useConversation from './useConversation';
import { useConversationStore } from '../ConversationStoreProvider';
import { IsMobileViewProvider } from './IsMobileViewContext';
import { useTokens } from '../tokens/TokensProvider';
import { resolveLocalizedText } from '../utils/localization';
// for testing
const initialMessages = undefined;
const INLINE_RESIZE_DELTA_THRESHOLD_PX = 8;
const INLINE_RESIZE_ROUNDING_STEP_PX = 4;
const INLINE_RESIZE_MIN_INTERVAL_MS = 250;
const INLINE_BORDER_SAFETY_PADDING_PX = 2;
const EmbeddedConversation = ({ adminOptions, agentAvatarImage, agentName, animatedProcessingImage, bodyStyle, closeButtonPosition, courseCardShowDescLines, courseCardShowImage, courseCardShowInstructor, courseCardShowTitle, defaultProcessingIcon, dividerStyle, enableMultiRefinements, enableVoiceInput, feedbackButton, footerStyle, footerLinks, footerText, welcomeScreenShellStyle, headerControlsColor, headerHeight = 50, topSpaceReserved = '0px', headerLogo, headerStyle, hideRightRailOnMobile, inputStyle, logoPosition, maxItemsInRightRail, menuButtonPosition, minimizeButtonPosition, minimizedLogo, multiRefinementSubmitButtonText, openWebPagesInNewTab, productFormatting, productPromptHeader, questionAlignment, questionStyle, referenceFormatting, resetConversationLabel, responseAlignment, responseStyle, sendImage, showItemReferencesWithMessage = false, hamburgerMenu, showResetWithInput, startingMessageFontStyle, userAvatarImage, windowStyle, disableAutoMinimize = false, initialMinimizedHeight, showPersistentPrompts = true, disableConversationPersistence = false, onProductClick, onPortalRelativeNavigate, initialMessage, hideInitialMessage = false, }) => {
    const [minimized, setMinimized] = useState(!disableAutoMinimize);
    const [hostForcedMinimized, setHostForcedMinimized] = useState(false);
    const [hostMinimizedHeight, setHostMinimizedHeight] = useState(initialMinimizedHeight);
    const hasPostedEmbedReadyRef = useRef(false);
    const inlineResizeRafRef = useRef(null);
    const inlineResizePendingForceRef = useRef(false);
    const lastInlinePostedHeightRef = useRef(null);
    const lastInlinePostedAtRef = useRef(0);
    const conversationStore = useConversationStore();
    const tokens = useTokens();
    const { stylesheet } = conversationStore;
    const customWelcomeEnabled = Boolean(getWelcomeScreenHtml(conversationStore.clientAIConfig?.welcomeScreenHtml));
    const resolvedFooterText = customWelcomeEnabled && typeof footerText === 'string' ? footerText.trim() : '';
    const shellStyle = customWelcomeEnabled && welcomeScreenShellStyle && typeof welcomeScreenShellStyle === 'object'
        ? welcomeScreenShellStyle : undefined;
    const styleImage = (image, style) => (image && style && typeof style === 'object' && !Array.isArray(style)
        ? { ...image, customStyle: style } : image);
    // Resolve resetConversationLabel with L10N (same pattern as header/minimized logo)
    const resolvedResetConversationLabel = useMemo(() => {
        const locale = conversationStore.customerContext?.currentPage?.locale
            || conversationStore.customerContext?.locale;
        const defaultLocale = conversationStore.defaultLocale || 'en';
        const resolved = resolveLocalizedText({
            legacyValue: resetConversationLabel,
            localizedMap: conversationStore.widgetProps?.resetConversationLabelL10N,
            enabled: conversationStore.widgetProps?.resetConversationLabelLocalized,
            locale,
            defaultLocale,
        });
        const trimmed = (resolved && typeof resolved === 'string') ? resolved.trim() : '';
        if (trimmed)
            return trimmed;
        const tokenValue = tokens?.getValue?.('resetConversationLabel');
        if (tokenValue && tokenValue !== '{{resetConversationLabel}}')
            return tokenValue;
        return 'New Conversation';
    }, [
        resetConversationLabel,
        conversationStore.widgetProps?.resetConversationLabelL10N,
        conversationStore.widgetProps?.resetConversationLabelLocalized,
        conversationStore.customerContext?.currentPage?.locale,
        conversationStore.customerContext?.locale,
        conversationStore.defaultLocale,
        tokens,
    ]);
    const logoAsset = styleImage(headerLogo, shellStyle?.headerLogo); // ? store.getOrLoad('asset', headerLogo) : undefined;
    const minimizedLogoAsset = styleImage(minimizedLogo, shellStyle?.minimizedLogo); // ? store.getOrLoad('asset', minimizedLogo) : undefined;
    const windowSx = useMemo(() => (buildSx(windowStyle, stylesheet, {
        height: '100%',
        minHeight: '0',
        display: 'flex',
        overflow: 'hidden',
        boxSizing: 'border-box',
        ...(disableAutoMinimize ? {
            margin: '0px',
            padding: `${INLINE_BORDER_SAFETY_PADDING_PX}px`,
        } : {}),
    })), [windowStyle, stylesheet, disableAutoMinimize]);
    const headerSx = useMemo(() => (buildSx(headerStyle, stylesheet, {
        justifyContent: 'space-between',
        alignContent: 'center',
        position: 'sticky',
        top: '0',
        zIndex: '10',
        height: `${headerHeight}px`,
        flexShrink: '0',
    })), [headerStyle, headerHeight, stylesheet]);
    const footerSx = useMemo(() => (buildSx(footerStyle, stylesheet, {
        justifyContent: resolvedFooterText ? 'space-between' : 'space-evenly',
    })), [footerStyle, resolvedFooterText, stylesheet]);
    const resolvedAgentName = agentName || `Embedded-${new Date().toISOString().slice(5, 7)}-${new Date().getFullYear()}`;
    const resetConversationModeEnabled = conversationStore.clientAIConfig?.resetConversationMode === true;
    const conversationConnect = useConversation({
        agentName: resolvedAgentName,
        layoutMode: 'ai',
        conversationType: 'shoppingAssistant',
        disableConversationPersistence,
        resetConversationOnNewInteraction: resetConversationModeEnabled,
    }, // options (shopping assistant must never be dual)
    adminOptions, initialMessages);
    const hasSentInitialMessage = useRef(false);
    const hasProcessedResetRef = useRef(false);
    const lastProcessedInitialMessageRef = useRef(null);
    useEffect(() => {
        if (initialMessage !== lastProcessedInitialMessageRef.current) {
            hasSentInitialMessage.current = false;
            hasProcessedResetRef.current = false;
        }
    }, [initialMessage]);
    useEffect(() => {
        if (hasSentInitialMessage.current) {
            return;
        }
        if (!initialMessage || !conversationConnect.append) {
            return;
        }
        if (!conversationConnect.conversation || !conversationConnect.conversation.id) {
            return;
        }
        try {
            const shouldReset = conversationConnect.shouldResetForNewSearch(initialMessage);
            if (conversationConnect.isDualSearchMode
                && !hasProcessedResetRef.current
                && shouldReset) {
                hasProcessedResetRef.current = true;
                conversationConnect.reset();
                return;
            }
            const normalizedQuery = initialMessage.trim().toLowerCase();
            const messages = conversationConnect.conversation.messages || [];
            const searchAlreadyExists = messages.some((msg) => msg?.role === 'user'
                && msg?.content?.trim().toLowerCase() === normalizedQuery);
            if (searchAlreadyExists) {
                hasSentInitialMessage.current = true;
                lastProcessedInitialMessageRef.current = initialMessage;
                return;
            }
            hasSentInitialMessage.current = true;
            lastProcessedInitialMessageRef.current = initialMessage;
            conversationConnect.append({
                role: 'user',
                content: initialMessage,
            });
        }
        catch (error) {
            console.error('[EmbeddedConversation] Error sending initial message:', error);
            hasSentInitialMessage.current = true;
            lastProcessedInitialMessageRef.current = initialMessage;
            if (conversationConnect.conversation && conversationConnect.conversation.id) {
                conversationConnect.append({
                    role: 'user',
                    content: initialMessage,
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        initialMessage,
        conversationConnect.conversation,
        conversationConnect.append,
        conversationConnect.isDualSearchMode,
        conversationConnect.shouldResetForNewSearch,
        conversationConnect.reset,
        disableConversationPersistence,
    ]);
    const handlers = {
        setMinimized: (data) => {
            if (disableAutoMinimize)
                return;
            const nextHeight = Number(data?.payload?.height ?? data?.height);
            const resolvedHeight = Number.isFinite(nextHeight) && nextHeight > 0
                ? nextHeight
                : hostMinimizedHeight;
            const alreadyForcedMinimized = minimized && hostForcedMinimized;
            if (alreadyForcedMinimized && (!resolvedHeight || resolvedHeight === hostMinimizedHeight)) {
                return;
            }
            setHostForcedMinimized(true);
            setMinimized(true);
            if (Number.isFinite(nextHeight) && nextHeight > 0) {
                setHostMinimizedHeight(nextHeight);
            }
        },
        setOpen: () => {
            if (disableAutoMinimize)
                return;
            if (!minimized && !hostForcedMinimized) {
                return;
            }
            setHostForcedMinimized(false);
            setMinimized(false);
        },
        customerContext: (data) => {
            const customerContext = data?.payload;
            if (!customerContext || typeof customerContext !== 'object')
                return;
            conversationStore.appendCustomerContext(customerContext);
        },
        shopperLoggedIn: (data) => {
            const isLoggedIn = data?.isLoggedIn ?? data?.payload?.isLoggedIn;
            if (typeof isLoggedIn !== 'boolean')
                return;
            if (!conversationStore.customerContext) {
                conversationStore.appendCustomerContext({ isLoggedIn });
            }
            else {
                conversationStore.customerContext.setLoggedIn(isLoggedIn);
            }
        },
        sendPrompt: (data) => {
            // console.log('sendPrompt', data);
            const prompt = data?.payload?.prompt;
            if (!prompt)
                return;
            const isProduct = conversationStore.customerContext?.currentPage?.isProduct;
            const isCollection = Boolean(conversationStore.customerContext?.currentPage?.isCollection) && !isProduct;
            if (conversationConnect.handleSuggestedPromptClick) {
                const type = isCollection ? 'category' : 'product';
                conversationConnect.handleSuggestedPromptClick(prompt, type, 'host_page');
            }
        },
    };
    const handlersRef = useRef(handlers);
    handlersRef.current = handlers;
    useEffect(() => {
        const onReceivedMessage = (event) => {
            // check the message source origin as a security measure
            // if (event.origin !== parentOrigin) {
            //   return;
            // }
            if (event.data?.source !== 'microcommerce-ai-embed')
                return;
            handlersRef.current[event.data.type]?.(event.data);
        };
        window.addEventListener('message', onReceivedMessage);
        if (!hasPostedEmbedReadyRef.current) {
            hasPostedEmbedReadyRef.current = true;
            window.parent.postMessage({
                source: 'microcommerce-ai-embed',
                type: 'embedReady',
            }, '*');
        }
        return () => {
            window.removeEventListener('message', onReceivedMessage);
        };
    }, []);
    useEffect(() => {
        if (disableAutoMinimize && minimized) {
            setMinimized(false);
        }
    }, [disableAutoMinimize, minimized]);
    useEffect(() => {
        if (!Number.isFinite(initialMinimizedHeight)) {
            return;
        }
        const nextHeight = Number(initialMinimizedHeight);
        if (nextHeight > 0) {
            setHostMinimizedHeight(nextHeight);
        }
    }, [initialMinimizedHeight]);
    useEffect(() => {
        if (disableAutoMinimize || typeof window === 'undefined') {
            return;
        }
        if (!hostMinimizedHeight || hostMinimizedHeight <= 0) {
            return;
        }
        // When iframe viewport matches minimized height, treat it as host-minimized at startup.
        if (window.innerHeight <= hostMinimizedHeight + 2) {
            setHostForcedMinimized(true);
            setMinimized(true);
        }
    }, [disableAutoMinimize, hostMinimizedHeight]);
    // Cross-origin iframes do not chain wheel scroll to the parent; forward at scroll edges (or when
    // there is no inner vertical scroller) so the host page can scroll.
    useEffect(() => {
        if (!disableAutoMinimize || typeof window === 'undefined') {
            return undefined;
        }
        if (!window.parent || window.parent === window) {
            return undefined;
        }
        const epsilon = 2;
        const findVerticalScrollable = (start) => {
            let el = start instanceof Element ? start : null;
            if (start && start instanceof Text && start.parentElement) {
                el = start.parentElement;
            }
            if (!el) {
                return null;
            }
            while (el && el !== document.documentElement) {
                const style = window.getComputedStyle(el);
                const oy = style.overflowY;
                const scrollableY = (oy === 'auto' || oy === 'scroll' || oy === 'overlay')
                    && el.scrollHeight > el.clientHeight + epsilon;
                if (scrollableY) {
                    return el;
                }
                el = el.parentElement;
            }
            const de = document.documentElement;
            if (de.scrollHeight > de.clientHeight + epsilon) {
                return de;
            }
            return null;
        };
        const postHostScroll = (deltaY) => {
            window.parent.postMessage({
                source: 'microcommerce-ai-embed',
                type: 'hostScroll',
                deltaY,
            }, '*');
        };
        const onWheel = (e) => {
            if (e.deltaY === 0) {
                return;
            }
            const scrollEl = findVerticalScrollable(e.target);
            if (scrollEl) {
                const { scrollTop, scrollHeight, clientHeight } = scrollEl;
                const atTop = scrollTop <= epsilon;
                const atBottom = scrollTop + clientHeight >= scrollHeight - epsilon;
                if (e.deltaY > 0 && atBottom) {
                    postHostScroll(e.deltaY);
                    e.preventDefault();
                    return;
                }
                if (e.deltaY < 0 && atTop) {
                    postHostScroll(e.deltaY);
                    e.preventDefault();
                    return;
                }
                return;
            }
            postHostScroll(e.deltaY);
            e.preventDefault();
        };
        window.addEventListener('wheel', onWheel, { passive: false, capture: true });
        return () => {
            window.removeEventListener('wheel', onWheel, { capture: true });
        };
    }, [disableAutoMinimize]);
    const resizeListener = useCallback(() => {
        if (disableAutoMinimize) {
            if (minimized) {
                setMinimized(false);
            }
            return;
        }
        if (hostForcedMinimized) {
            return;
        }
        if (window.innerHeight < headerHeight + 40 && !minimized) {
            setMinimized(true);
        }
        else if (window.innerHeight >= headerHeight + 40 && minimized) {
            setMinimized(false);
        }
    }, [disableAutoMinimize, headerHeight, minimized, hostForcedMinimized]);
    // add a resize listener to update minimized
    useEffect(() => {
        window?.addEventListener('resize', resizeListener);
        return () => {
            window?.removeEventListener('resize', resizeListener);
        };
    }, [headerHeight, resizeListener]);
    useEffect(() => {
        resizeListener();
    }, [resizeListener]);
    const scheduleInlineResize = useCallback((force = false) => {
        if (!disableAutoMinimize)
            return;
        if (typeof window === 'undefined' || !window.parent)
            return;
        if (inlineResizeRafRef.current !== null) {
            if (inlineResizePendingForceRef.current && !force) {
                return;
            }
            window.cancelAnimationFrame(inlineResizeRafRef.current);
        }
        inlineResizePendingForceRef.current = force;
        inlineResizeRafRef.current = window.requestAnimationFrame(() => {
            inlineResizeRafRef.current = null;
            inlineResizePendingForceRef.current = false;
            const root = document.querySelector('.embedded-conversation-widget');
            const rootHeight = Math.max(root?.offsetHeight || 0, root?.getBoundingClientRect?.().height || 0);
            const messageCount = conversationStore.currentConversation?.messages?.length || 0;
            // Measure overflow in the real message stack/empty-state containers, not the 100%-height wrappers.
            const overflowCandidates = root
                ? Array.from(root.querySelectorAll('[data-mcn-streaming-scroll="1"], [data-mcn-empty-state-scroll="1"]'))
                : [];
            let maxOverflowDelta = 0;
            let minUnderflowDelta = 0;
            overflowCandidates.forEach((el) => {
                const delta = (el.scrollHeight || 0) - (el.clientHeight || 0);
                if (delta > maxOverflowDelta) {
                    maxOverflowDelta = delta;
                }
                if (delta < minUnderflowDelta) {
                    minUnderflowDelta = delta;
                }
            });
            const isEmptyStateVisible = Boolean(root?.querySelector('[data-mcn-empty-state-scroll="1"]'));
            let resizeDelta = 0;
            if (maxOverflowDelta > 0) {
                resizeDelta = maxOverflowDelta;
            }
            else if (force && isEmptyStateVisible) {
                resizeDelta = minUnderflowDelta;
            }
            if (!force && messageCount === 0 && maxOverflowDelta <= 0) {
                return;
            }
            const measuredHeight = Math.max(rootHeight + resizeDelta, 120);
            const nextHeight = Math.ceil(measuredHeight / INLINE_RESIZE_ROUNDING_STEP_PX) * INLINE_RESIZE_ROUNDING_STEP_PX;
            if (!Number.isFinite(nextHeight) || nextHeight <= 0)
                return;
            const previousHeight = lastInlinePostedHeightRef.current;
            const delta = previousHeight === null ? Number.POSITIVE_INFINITY : Math.abs(nextHeight - previousHeight);
            const now = Date.now();
            const elapsed = now - lastInlinePostedAtRef.current;
            if (!force && previousHeight !== null && delta < INLINE_RESIZE_DELTA_THRESHOLD_PX)
                return;
            if (!force && elapsed < INLINE_RESIZE_MIN_INTERVAL_MS)
                return;
            lastInlinePostedHeightRef.current = nextHeight;
            lastInlinePostedAtRef.current = now;
            window.parent.postMessage({
                source: 'microcommerce-ai-embed',
                type: 'expandIframe',
                target: 'inline',
                mode: 'expanded',
                height: nextHeight,
            }, '*');
        });
    }, [disableAutoMinimize, conversationStore.currentConversation?.messages?.length]);
    useEffect(() => {
        if (!disableAutoMinimize)
            return undefined;
        scheduleInlineResize(true);
        const onResize = () => {
            scheduleInlineResize();
        };
        window.addEventListener('resize', onResize);
        const resizeObserver = typeof ResizeObserver !== 'undefined'
            ? new ResizeObserver(() => {
                scheduleInlineResize();
            })
            : null;
        const resizeTargets = [
            document.body,
            document.documentElement,
            document.querySelector('.embedded-conversation-widget'),
        ].filter((target) => Boolean(target));
        resizeTargets.forEach((target) => resizeObserver?.observe(target));
        const mutationObserver = typeof MutationObserver !== 'undefined'
            ? new MutationObserver(() => {
                scheduleInlineResize();
            })
            : null;
        mutationObserver?.observe(document.body, {
            childList: true,
            subtree: true,
            characterData: true,
        });
        return () => {
            window.removeEventListener('resize', onResize);
            resizeObserver?.disconnect();
            mutationObserver?.disconnect();
            if (inlineResizeRafRef.current !== null) {
                window.cancelAnimationFrame(inlineResizeRafRef.current);
                inlineResizeRafRef.current = null;
            }
            inlineResizePendingForceRef.current = false;
        };
    }, [disableAutoMinimize, scheduleInlineResize]);
    useEffect(() => {
        if (!disableAutoMinimize)
            return;
        scheduleInlineResize(true);
    }, [
        disableAutoMinimize,
        minimized,
        conversationStore.currentConversation?.messages?.length,
        conversationStore.suggestedPrompts?.length,
        conversationStore.smartSummary?.bullets?.length,
        conversationStore.isLoadingSuggestedPrompts,
        scheduleInlineResize,
    ]);
    const handleResetConversation = useCallback(() => {
        conversationConnect.reset();
        if (!disableAutoMinimize || typeof window === 'undefined')
            return;
        // Force immediate + delayed resize to collapse inline iframe after reset.
        scheduleInlineResize(true);
        window.requestAnimationFrame(() => {
            scheduleInlineResize(true);
        });
        window.setTimeout(() => {
            scheduleInlineResize(true);
        }, INLINE_RESIZE_MIN_INTERVAL_MS);
    }, [conversationConnect, disableAutoMinimize, scheduleInlineResize]);
    // const minimized = typeof window === 'undefined' || window.innerHeight < headerHeight + 10;
    // if (typeof window !== 'undefined' && window.innerHeight < headerHeight + 10 && !minimized) {
    //   console.log('setting minimized to true');
    //   setMinimized(true);
    // }
    return (_jsx(IsMobileViewProvider, { children: _jsxs(Stack, { sx: windowSx, direction: "column", className: "embedded-conversation-widget", children: [_jsx(EmbeddedHeader, { customWelcomeEnabled: customWelcomeEnabled, amplifyEnvironment: conversationStore.amplifyEnvironment, logoAsset: logoAsset, minimizedLogoAsset: minimizedLogoAsset, logoPosition: logoPosition, minimized: minimized, menuButtonPosition: menuButtonPosition, minimizeButtonPosition: minimizeButtonPosition, closeButtonPosition: closeButtonPosition, 
                    // setMinimized={setMinimized}
                    headerSx: headerSx, headerHeight: headerHeight, minimizedHeight: hostMinimizedHeight, headerControlsColor: replaceStylesheetGlobals(stylesheet, headerControlsColor || '#ffffff'), resetConversationLabel: resolvedResetConversationLabel, triggerReset: handleResetConversation, hamburgerMenu: hamburgerMenu }), !minimized && (_jsxs(_Fragment, { children: [_jsx(Divider, { sx: {
                                borderTop: `${dividerStyle.thickness}px solid ${replaceStylesheetGlobals(stylesheet, dividerStyle.color)}`,
                                borderBottom: 0,
                            } }), _jsx(Stack, { sx: {
                                flex: 1,
                                minHeight: 0,
                                overflow: 'hidden',
                                justifyContent: 'flex-start',
                            }, direction: "column", children: _jsx(Box, { sx: {
                                    flex: 1,
                                    minHeight: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    overflow: 'hidden',
                                }, children: _jsx(Conversation, { limitToPageWidth: false, showItemReferencesWithMessage: showItemReferencesWithMessage, showItemReferencesCarousel: false, showResetWithInput: showResetWithInput, courseCardShowImage: courseCardShowImage, courseCardShowTitle: courseCardShowTitle, courseCardShowInstructor: courseCardShowInstructor, courseCardShowDescLines: courseCardShowDescLines, openWebPagesInNewTab: openWebPagesInNewTab, style: bodyStyle, questionStyle: questionStyle, responseStyle: responseStyle, feedbackButton: feedbackButton, questionAlignment: questionAlignment, responseAlignment: responseAlignment, referenceFormatting: referenceFormatting, inputStyle: inputStyle, startingMessageFontStyle: startingMessageFontStyle, adminOptions: adminOptions, sendImage: styleImage(sendImage, shellStyle?.sendImage), enableVoiceInput: enableVoiceInput, agentAvatarImage: agentAvatarImage, userAvatarImage: userAvatarImage, resetConversationLabel: resolvedResetConversationLabel, conversationConnect: conversationConnect, hideRightRailOnMobile: hideRightRailOnMobile, enableMultiRefinements: enableMultiRefinements, multiRefinementSubmitButtonText: multiRefinementSubmitButtonText, maxItemsInRightRail: maxItemsInRightRail, animatedProcessingImage: animatedProcessingImage, productFormatting: productFormatting, productPromptHeader: productPromptHeader, defaultProcessingIcon: defaultProcessingIcon, onProductClick: onProductClick, onPortalRelativeNavigate: onPortalRelativeNavigate, topSpaceReserved: topSpaceReserved, showPersistentPrompts: showPersistentPrompts, disableAddSpaceAnimation: disableAutoMinimize, hideFirstMessage: hideInitialMessage }) }) }), _jsxs(Stack, { sx: resolvedFooterText
                                ? { flexShrink: 0, flexWrap: 'wrap', alignItems: 'center', gap: 1, ...footerSx }
                                : footerSx, direction: "row", children: [footerLinks?.links?.map((link) => customWelcomeEnabled && link.action === 'openSupportChat' ? (_jsx(Link, { component: "button", type: "button", onClick: () => window?.top?.postMessage({ source: 'microcommerce-ai-embed', type: 'openSupportChat' }, '*'), sx: {
                                        color: replaceStylesheetGlobals(stylesheet, footerStyle.color),
                                    }, children: link.label })) : (_jsx(Link, { href: link.url, target: "_blank", rel: "noreferrer", sx: {
                                        color: replaceStylesheetGlobals(stylesheet, footerStyle.color),
                                    }, children: link.label }))), resolvedFooterText ? _jsx(Typography, { variant: "caption", sx: { color: '#757575', fontSize: '0.875rem' }, children: resolvedFooterText }) : null] })] }))] }) }));
};
// EmbeddedConversation.getData = async (
//   {
//     headerLogo,
//     minimizedLogo,
//   }: any,
//   store: any,
// ) => {
//   console.log('EmbeddedConversation.getData', {
//     headerLogo,
//     minimizedLogo,
//   });
//   const promises = await Promise.all([
//     EmbeddedHeader.getData({
//       logoAsset: headerLogo,
//       minimizedLogoAsset: minimizedLogo,
//     }, store),
//     Conversation.getData(),
//   ]);
//   return promises;
// };
EmbeddedConversation.defaultProps = {
    headerLogo: undefined,
    minimizedLogo: undefined,
    logoPosition: 'center',
    headerHeight: 50,
    menuButtonPosition: 'right',
    minimizeButtonPosition: 'left',
    closeButtonPosition: 'hide',
    windowStyle: {
        backgroundColor: 'color_secondary',
        borderColor: 'color_primary',
        borderRadius: 4,
        borderWidth: 0,
        color: 'color_font',
        margin: 0,
        padding: 4,
        shadow: 0,
    },
    headerStyle: {
        backgroundColor: 'color_secondary',
        borderColor: 'color_primary',
        borderRadius: 2,
        borderWidth: 0,
        color: 'color_font',
        margin: 0,
        padding: 4,
        shadow: 0,
    },
    headerControlsColor: 'color_primary',
    dividerStyle: {
        thickness: 0,
        color: 'color_primary',
    },
    bodyStyle: {
        backgroundColor: 'color_background',
        borderColor: 'color_primary',
        borderRadius: 2,
        borderWidth: 0,
        color: 'color_font',
        margin: 0,
        padding: 4,
        shadow: 0,
    },
    footerStyle: {
        backgroundColor: 'color_secondary',
        borderColor: 'color_primary',
        borderRadius: 2,
        borderWidth: 0,
        color: 'color_font',
        margin: 0,
        padding: 2,
        shadow: 0,
    },
    footerText: '',
    footerLinks: {
        title: '',
        links: [],
    },
    agentName: undefined,
    disableAutoMinimize: false,
    disableConversationPersistence: false,
    ...Conversation.defaultProps,
    showItemReferencesWithMessage: false,
};
EmbeddedConversation.meta = {
    group: 'portal',
    name: 'Embedded AI Conversation',
    sort: 40,
    description: 'Conversation widget with header and footer for inclusion in 3rd party sites.',
    props: [
        {
            key: 'title',
            name: 'Window Title',
            type: 'text',
            description: 'Title for dialog.',
        },
        {
            key: 'titlePosition',
            name: 'Title Position',
            type: 'array',
            items: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
            ],
            description: 'Position of title in the header.',
        },
        {
            key: 'headerLogo',
            name: 'Logo Image',
            type: 'imageAsset',
            description: 'Logo to show in header.',
        },
        {
            key: 'minimizedLogo',
            name: 'Minimized Logo Image',
            type: 'imageAsset',
            description: 'Logo to show when the widget is minimized.',
        },
        {
            key: 'logoPosition',
            name: 'Logo Position',
            type: 'array',
            items: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
            ],
            description: 'Position of logo in the header.',
        },
        {
            key: 'headerHeight',
            name: 'Header Height',
            type: 'number',
            description: 'Height of the header in pixels.',
            min: 20,
            max: 300,
        },
        {
            key: 'menuButtonPosition',
            name: 'Menu Button Position',
            type: 'array',
            items: [
                { value: 'left', label: 'Left' },
                { value: 'right', label: 'Right' },
                { value: 'hide', label: 'Hide' },
            ],
            description: 'Position of menu button in the header.',
        },
        {
            key: 'minimizeButtonPosition',
            name: 'Minimize Button Position',
            type: 'array',
            items: [
                { value: 'left', label: 'Left' },
                { value: 'right', label: 'Right' },
                { value: 'hide', label: 'Hide' },
            ],
            description: 'Position of minimize button in the header.',
        },
        {
            key: 'closeButtonPosition',
            name: 'Close Button Position',
            type: 'array',
            items: [
                { value: 'left', label: 'Left' },
                { value: 'right', label: 'Right' },
                { value: 'hide', label: 'Hide' },
            ],
            description: 'Position of close button in the header.',
        },
        {
            key: 'windowStyle',
            name: 'Window Style',
            type: 'boxStyle',
            description: 'Style of window.',
            group: 'style',
        },
        {
            key: 'bodyStyle',
            name: 'Body Style',
            type: 'boxStyle',
            description: 'Style of the body area of the chat.',
            group: 'style',
        },
        {
            key: 'headerStyle',
            name: 'Header Style',
            type: 'boxStyle',
            description: 'Style of window header.',
            group: 'style',
        },
        {
            key: 'headerControlsColor',
            name: 'Header Controls Color',
            type: 'color',
            description: 'Color of header controls.',
            group: 'style',
        },
        {
            key: 'dividerStyle.color',
            name: 'Divider Color',
            type: 'color',
            description: 'Color of divider between header and body.',
            group: 'style',
        },
        {
            key: 'dividerStyle.thickness',
            name: 'Divider Thickness',
            type: 'number',
            description: 'Thickness of divider between header and body.',
            group: 'style',
        },
        {
            key: 'footerStyle',
            name: 'Footer Style',
            type: 'boxStyle',
            description: 'Style of window footer.',
            group: 'style',
        },
        {
            key: 'footerText',
            name: 'Footer Text',
            type: 'text',
            description: 'Optional plain text below the conversation input.',
        },
        {
            key: 'footerLinks',
            name: 'Footer Links',
            type: 'linkList',
            description: 'Footer links',
        },
        {
            key: 'showItemReferencesWithMessage',
            name: 'Show Item References With Message',
            type: 'boolean',
            description: 'Displays relevent content next to the message.',
        },
        {
            key: 'agentName',
            name: 'Agent Name',
            type: 'text',
            description: 'Optional agent identifier for AI reporting.',
        },
        ...sharedConversationMeta,
    ],
    defaultConfig: {
        type: 'component',
        component: 'EmbeddedConversation',
        props: EmbeddedConversation.defaultProps,
    },
};
export default observer(EmbeddedConversation);
//# sourceMappingURL=EmbeddedConversation.js.map